import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Response, Pagination } from '@ui/src';
import { customersComponentApi } from '../../../customers/store/services/customers';
import {
  Beneficiary,
  BeneficiaryContract,
  BeneficiaryStatusLogs,
} from '../../beneficiaries/types';

export interface CustomerBeneficiariesState {
  beneficiaries?: Beneficiary[];
  beneficiariesErrorsList?: string;
  pagination?: Pagination;
  beneficiary?: Beneficiary;
  beneficiaryStatusLogs?: BeneficiaryStatusLogs[];
  beneficiaryContracts?: BeneficiaryContract[];
  beneficiaryContractsErrorsList?: string;
  beneficiaryContract?: BeneficiaryContract;
  documentsCategories?: {
    name: string;
    id: string;
  }[];
}

const initialState: CustomerBeneficiariesState = {};

export const CustomerBeneficiarieslice = createSlice({
  name: 'customerBeneficiaries',
  initialState,
  reducers: {
    clearBeneficiaryState: (state) => {
      state.beneficiary = undefined;
      state.beneficiaryStatusLogs = undefined;
      state.beneficiaryContracts = undefined;
    },
    clearBeneficiariesState: (state) => {
      state.beneficiaries = undefined;
      state.beneficiariesErrorsList = undefined;
      state.pagination = undefined;
      state.beneficiary = undefined;
      state.beneficiaryStatusLogs = undefined;
      state.beneficiaryContracts = undefined;
      state.beneficiaryContract = undefined;
      state.beneficiaryContractsErrorsList = undefined;
    },
    clearContractState: (state) => {
      state.beneficiaryContract = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerBeneficiaries.matchPending,
      (state) => {
        state.beneficiariesErrorsList = undefined;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerBeneficiaries.matchRejected,
      (state, action: any) => {
        state.beneficiaries = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.beneficiariesErrorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.beneficiariesErrorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerBeneficiaries.matchFulfilled,
      (state, action: PayloadAction<Response<Beneficiary>>) => {
        state.beneficiaries = action.payload.data.edges.map((item) => ({
          ...item.node,
          search: item.node.name,
        }));
        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerBeneficiary.matchFulfilled,
      (state, action: PayloadAction<{ data: Beneficiary }>) => {
        state.beneficiary = action.payload.data;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersBeneficairyDocumentsCategories
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<{ id: string; name: string }>>,
      ) => {
        state.documentsCategories = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerBeneficiaryStatusLog
        .matchFulfilled,
      (state, action: PayloadAction<Response<BeneficiaryStatusLogs>>) => {
        state.beneficiaryStatusLogs = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerBeneficiaryContracts
        .matchPending,
      (state) => {
        state.beneficiaryContractsErrorsList = undefined;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerBeneficiaryContracts
        .matchRejected,
      (state, action: any) => {
        state.beneficiaryContracts = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.beneficiaryContractsErrorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.beneficiaryContractsErrorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerBeneficiaryContracts
        .matchFulfilled,
      (state, action: PayloadAction<Response<BeneficiaryContract>>) => {
        state.beneficiaryContracts = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateBeneficiaryStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: Beneficiary }>) => {
        state.beneficiary = action.payload.data;

        state.beneficiaries = state?.beneficiaries?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.status = action.payload.data.status;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateContract.matchFulfilled,
      (state, action: PayloadAction<{ data: BeneficiaryContract }>) => {
        state.beneficiaryContract = {
          ...action.payload.data,
          transactionsSum: state.beneficiaryContract?.transactionsSum,
        };

        state.beneficiaryContracts = state?.beneficiaryContracts?.map(
          (item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          },
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateContractStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: BeneficiaryContract }>) => {
        state.beneficiaryContract = {
          ...action.payload.data,
          transactionsSum: state.beneficiaryContract?.transactionsSum,
        };

        state.beneficiaryContracts = state?.beneficiaryContracts?.map(
          (item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          },
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomerBeneficiaryContract
        .matchFulfilled,
      (state, action: PayloadAction<{ data: BeneficiaryContract }>) => {
        state.beneficiaryContract = action.payload.data;
      },
    );
  },
});
export const {
  clearBeneficiariesState,
  clearBeneficiaryState,
  clearContractState,
} = CustomerBeneficiarieslice.actions;

export default CustomerBeneficiarieslice.reducer;
